// SceneDesc -> three.js objects, posing at time t, and the browser depth pass.

import * as THREE from 'three';
import { renderDepth } from '../depth/render';
import type { DepthData } from '../types';
import { geometryFor } from './geometry';
import { evaluate } from './timeline';
import type { Camera, SceneDesc, Transform } from './types';

const deg = THREE.MathUtils.degToRad;

export interface Built {
  root: THREE.Group;
  objects: Map<string, THREE.Object3D>;
}

/** Builds the node hierarchy; meshes get `material` (shared). */
export function buildObjects(scene: SceneDesc, material: THREE.Material): Built {
  const root = new THREE.Group();
  const objects = new Map<string, THREE.Object3D>();
  for (const n of scene.nodes) {
    const obj = n.shape.kind === 'group' ? new THREE.Group() : new THREE.Mesh(geometryFor(n.shape, scene), material);
    obj.name = n.name || n.id;
    obj.userData.id = n.id;
    obj.visible = n.visible;
    applyTransform(obj, n.transform);
    objects.set(n.id, obj);
  }
  for (const n of scene.nodes) {
    const obj = objects.get(n.id)!;
    const parent = n.parent ? objects.get(n.parent) : null;
    (parent ?? root).add(obj);
  }
  return { root, objects };
}

export function applyTransform(obj: THREE.Object3D, t: Transform) {
  obj.position.set(...t.position);
  obj.rotation.set(deg(t.rotation[0]), deg(t.rotation[1]), deg(t.rotation[2]), 'XYZ');
  obj.scale.set(...t.scale);
}

export function readTransform(obj: THREE.Object3D): Transform {
  const r = (v: number) => Math.round(v * 1e4) / 1e4;
  const d = (v: number) => r(THREE.MathUtils.radToDeg(v));
  return {
    position: [r(obj.position.x), r(obj.position.y), r(obj.position.z)],
    rotation: [d(obj.rotation.x), d(obj.rotation.y), d(obj.rotation.z)],
    scale: [r(obj.scale.x), r(obj.scale.y), r(obj.scale.z)],
  };
}

/** Poses objects for time `t`; returns the animated camera. */
export function pose(scene: SceneDesc, objects: Map<string, THREE.Object3D>, t: number): Camera {
  const p = evaluate(scene, t);
  for (const [id, tr] of Object.entries(p.local)) {
    const obj = objects.get(id);
    if (obj) applyTransform(obj, tr);
  }
  return p.camera;
}

export function threeCamera(c: Camera, aspect: number): THREE.Camera {
  let cam: THREE.PerspectiveCamera | THREE.OrthographicCamera;
  if (c.projection === 'orthographic') {
    const hh = c.height / 2;
    cam = new THREE.OrthographicCamera(-hh * aspect, hh * aspect, hh, -hh, c.near, c.far);
  } else {
    cam = new THREE.PerspectiveCamera(c.fov, aspect, c.near, c.far);
  }
  cam.position.set(...c.position);
  cam.up.set(0, 1, 0);
  cam.lookAt(new THREE.Vector3(...c.target));
  cam.updateMatrixWorld(true);
  cam.updateProjectionMatrix();
  return cam;
}

/** Linear depth of the scene at time `t` (near -> 1, far -> 0, background 0). */
export function renderSceneDepth(scene: SceneDesc, t: number, width: number, height: number): DepthData {
  const material = new THREE.MeshBasicMaterial();
  const { root, objects } = buildObjects(scene, material);
  const s = new THREE.Scene();
  s.add(root);
  const c = pose(scene, objects, t);
  s.updateMatrixWorld(true);
  const cam = threeCamera(c, width / height);
  const depth = renderDepth(
    s,
    cam,
    { mode: 'view', near: c.near, far: c.far, base: c.base ?? 0, top: c.top ?? 1 },
    width,
    height,
    2,
  );
  root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
  material.dispose();
  return depth;
}

/** Depth frames for the scene timeline. */
export async function renderSceneAnimation(
  scene: SceneDesc,
  fps: number,
  frames: number,
  width: number,
  height: number,
  onFrame: (done: number) => void,
  signal?: AbortSignal,
): Promise<DepthData[]> {
  const material = new THREE.MeshBasicMaterial();
  const { root, objects } = buildObjects(scene, material);
  const s = new THREE.Scene();
  s.add(root);
  const out: DepthData[] = [];
  try {
    for (let i = 0; i < frames; i++) {
      if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
      const c = pose(scene, objects, i / fps);
      s.updateMatrixWorld(true);
      const cam = threeCamera(c, width / height);
      out.push(
        renderDepth(
          s,
          cam,
          { mode: 'view', near: c.near, far: c.far, base: c.base ?? 0, top: c.top ?? 1 },
          width,
          height,
          2,
        ),
      );
      onFrame(i + 1);
      if (i % 2) await new Promise((r) => setTimeout(r, 0));
    }
  } finally {
    root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
    material.dispose();
  }
  return out;
}
